import { useCallback, useEffect, useRef } from 'react';
import { ThreadGestureClock } from './use-thread-gesture-clock';

const GESTURE_WINDOW_MS = 1000;

/*
  Tells load-more whether a live user gesture happened recently, and feeds upward
  wheels to the paginate check directly.

  Both exist for the same reason: a merged page changes scrollHeight, and near the
  top the browser's scroll anchoring emits its own scroll events while the viewport
  still sits inside the load-more threshold — without the gate that chains fetch
  after fetch with no user input until the whole thread is loaded. And pinned at the
  very top, an upward wheel cannot move scrollTop, so no scroll event fires at all —
  the wheel listener re-runs the threshold check itself.
*/
export function useLoadMoreGestureGate(
  clock: ThreadGestureClock,
  onUpwardWheel: (el: HTMLElement) => void,
): { isGestureFresh: () => boolean } {
  const onUpwardWheelRef = useRef(onUpwardWheel);
  onUpwardWheelRef.current = onUpwardWheel;

  useEffect(
    () =>
      clock.subscribe((event) => {
        if (
          event instanceof WheelEvent &&
          event.deltaY < 0 &&
          event.currentTarget instanceof HTMLElement
        ) {
          onUpwardWheelRef.current(event.currentTarget);
        }
      }),
    [clock],
  );

  const isGestureFresh = useCallback(
    () => performance.now() - clock.lastGestureAt() <= GESTURE_WINDOW_MS,
    [clock],
  );

  return { isGestureFresh };
}
